/**
 * This is a SwatchDropDown. This should be Displaying the Different option Swatches . The Selected Swatch Will be Highlighted on the Click of the Option.This needs to be Provided with the List of Options
 *
 * @module views/components/SwatchDropDown
 * @memberof -Common
 */
import './SwatchDropDown.scss';

import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Text from '@ulta/core/components/Text/Text';
import UseKeyPress from '@ulta/core/hooks/useKeyPress/UseKeyPress';
import useOutsideClick from '@ulta/core/hooks/useOutsideClick/useOutsideClick';
import { useToggle } from '@ulta/core/hooks/useToggle/useToggle';
import datacapture from '@ulta/core/utils/datacapture/datacapture' ;

import { constants } from '@ulta/utils/constants/constants';

import Swatch from '../Swatch/Swatch';
import SwatchDropDownItem from '../SwatchDropDownItem/SwatchDropDownItem';

const { SPACE_KEY, UPARROW_KEY, DOWNARROW_KEY, ENTER_KEY } = constants;

/**
  * Represents a SwatchDropDown component
  *
  * @method
  * @param {SwatchDropDownProps} props - React properties passed from composition
  * @returns SwatchDropDown
  */
export const SwatchDropDown = function( props ){
  const [listOpen, setListOpen] = useToggle( false );
  const [actElm, setActElm] = useState( 0 );
  const {
    variants,
    typeLabel,
    selectAction
  } = props;
  const selectedItem = variants.length > 0 && variants.find( variant => variant.selected ) || variants[0];
  const headerRef = useRef( );
  const listRef = useRef( );
  const containerRef = useRef( false );
  const onItemSelection = ( item ) => {
    selectAction?.( item );
    if( item.selectAction.dataCaptureData ){
      triggerDatacaptureEvent( item.selectAction.dataCaptureData );
    }
    headerRef.current.focus();
  };

  const closeDropDown = composeCloseDropDown( listRef, containerRef, setListOpen );

  useOutsideClick( { watcher: listOpen, el: listRef.current }, { handleClick: () => {
    setListOpen( false );
  } } );

  UseKeyPress( [SPACE_KEY, UPARROW_KEY, DOWNARROW_KEY, ENTER_KEY], headerRef, () => setListOpen( true ) );
  return (
    <div className='SwatchDropDown'
      ref={ containerRef }
    >
      { /* A11y requirements are handle by the UseKeyPress  */ }
      { /* eslint-disable-next-line jsx-a11y/click-events-have-key-events */ }
      <div
        className='SwatchDropDown__header'
        onClick={ () => setListOpen( !listOpen ) }
        role='combobox'
        aria-label='product swatches'
        aria-expanded={ listOpen }
        aria-controls='SwatchDropDownItems'
        aria-haspopup='listbox'
        tabIndex='0'
        ref={ headerRef }
        data-testid='swatch__header'
      >
        { typeLabel && (
          <Text htmlTag='span'
            textStyle='body-3'
            color='neutral-700'
          >{ `${typeLabel}: ` }</Text>
        ) }
        <div className='SwatchDropDown__title'>
          <Swatch src={ selectedItem.swatchImage?.imageUrl }
            alt={ selectedItem.swatchImage?.description }
            height={ 40 }
            width={ 40 }
            disabled={ selectedItem.unavailable }
          />
          <div className='SwatchDropDown__nameDescription'>
            <Text htmlTag='span'
              textStyle='body-3'
            >{ selectedItem.name }</Text>
            <div className='SwatchDropDown__description'>
              <Text htmlTag='span'
                textStyle='body-3'
                color='neutral-600'
              >{ selectedItem?.shadeDescription }</Text>
            </div>

          </div>
          <i className='SwatchDropDown__icon'></i>
        </div>
      </div>
      <div className={ classNames( 'SwatchDropDown__listContainer', {
        'SwatchDropDown__listContainer--open': listOpen
      } ) }
      ref={ listRef }
      >
        <ul
          role='listbox'
          aria-label='swatches'
          id='SwatchDropDownItems'
        >
          { listOpen && variants.map( ( item, index ) => (
            <li key={ index }>
              <SwatchDropDownItem
                name={ item.name }
                shadeDescription={ item.shadeDescription }
                image={ item.swatchImage?.imageUrl }
                altText={ item.swatchImage?.description }
                selected={ item.selected }
                unavailable={ item.unavailable }
                disabled={ item.disabled }
                onChange={ () => {
                  setListOpen( false );
                  onItemSelection( item );
                } }
                setListOpen={ setListOpen }
                actElm={ actElm }
                headerRef={ headerRef }
                setActElm={ setActElm }
                activeCheck={ actElm === index }
                index={ index }
                itemLength={ variants.length }
              />
            </li>
          ) ) }
        </ul>
      </div>
    </div>
  );
};

/**
 * @method triggerDatacaptureEvent
 * @summary This method triggers datacapture events
 * @param { Object } data muation response
 */
export const triggerDatacaptureEvent = ( datacaptureEvent ) =>{
  if( datacaptureEvent ){
    datacapture.processEvents( { dataCapture : datacaptureEvent }, datacaptureEvent.clientEvent.toLowerCase() );
  }
};

/**
 * Composes the on outside click handler
 *
 * @param {object} listRef React ref for list dropdown
 * @param {object} containerRef React ref for dropdown container
 * @param {function} setListOpen useState setter for list open/close boolean
 * @returns {function} Close list on outside click handler
 */
export const composeCloseDropDown = ( listRef, containerRef, setListOpen ) => ( e ) => {
  let clickedInsideList = e.target === listRef.current;
  let el = !clickedInsideList && e.target.parentNode;

  while ( el ){
    if( el === listRef.current ){
      clickedInsideList = true;
    }

    el = el !== containerRef && el.parentElement;
  }

  if( clickedInsideList ){
    return;
  }

  setListOpen( false );
};

/**
 * Property type definitions
 * @typedef SwatchDropDownProps
 * @type {object}
 * @property {object.<{ productId: string, skuId: string, name: string, shadeDescription: string, swatchImage: object, smooshImage: object, selected: boolean, disabled: boolean, unavailable: boolean, listPrice: string, salePrice: string, selectAction: object}>} variants - Sets the information of product's variants
 * @property {string} typeLabel - sets the swatch dropdown label text
 */
export const propTypes = {
  variants: PropTypes.arrayOf( PropTypes.shape(
    {
      productId: PropTypes.string,
      skuId: PropTypes.string,
      name: PropTypes.string,
      shadeDescription: PropTypes.string,
      swatchImage: PropTypes.shape( {
        imageUrl: PropTypes.string,
        description: PropTypes.string
      } ),
      smooshImage: PropTypes.shape( {
        imageUrl: PropTypes.string,
        description: PropTypes.string
      } ),
      selected: PropTypes.bool,
      disabled: PropTypes.bool,
      unavailable: PropTypes.bool,
      listPrice: PropTypes.string,
      salePrice: PropTypes.string,
      selectAction: PropTypes.shape( {
        label: PropTypes.string,
        url: PropTypes.string,
        graphql: PropTypes.string
      } )
    }
  ) ).isRequired,
  typeLabel: PropTypes.string,
  selectAction: PropTypes.func
};

SwatchDropDown.propTypes = propTypes;

export default SwatchDropDown;